import React, { lazy, Suspense, useEffect } from 'react';
import i18n from '@/utils/i18n';
import { useTranslation } from 'react-i18next';
import { LangKey as awxLangKey } from '@airwallex/global';
import { ThemeProvider } from '@airwallex/theme';
import { getLanguage } from '@/utils/language';
import { StoreProvider } from '@/providers/storeProvider';
import { ThemeDataProvider } from '@/providers/themeDataProvider';
import { AppThemeShell } from '@/providers/appThemeShell';
import Route from './pages';
import { airTracker } from './utils/airTracker';
import { StoreProviderV2 } from './pages/paybylinkV2/providers/storeProvider';
import StatsigProviderV2 from './pages/paybylinkV2/providers/statsigProvider';
import { ToastProvider } from '@airwallex/webapp-components';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorPage from './pages/error/paymentLinkErrorV2';
import { BrowserRouter, Route as RouterRoute, Switch } from 'react-router-dom';
import Loading from '@/components/skeleton';

export const COMPONENT_NAME_V2 = 'PBL_FE_V2';

const isPreviewRoute = window.location.pathname.startsWith('/pay/preview/');

const PreviewInvoice = lazy(() => import('./pages/checkout/preview'));
const PreviewPayByLink = lazy(() => import('./pages/paybylinkV2/preview'));

// Lightweight shell for preview routes — no Statsig, no store providers.
// Preview pages set up their own context via PreviewProvider.
const PreviewApp: React.FC = () => {
  useEffect(() => {
    i18n.changeLanguage('en');
  }, []);

  return (
    <ThemeProvider langKey="en">
      <BrowserRouter>
        <Suspense fallback={<Loading />}>
          <Switch>
            <RouterRoute path="/pay/preview/xero" exact component={PreviewInvoice} />
            <RouterRoute path="/pay/preview/paybylink" exact component={PreviewPayByLink} />
          </Switch>
        </Suspense>
      </BrowserRouter>
    </ThemeProvider>
  );
};

const App: React.FC = () => {
  useEffect(() => {
    const langKey = getLanguage();
    airTracker.updateCommonData({ lang: langKey });
    i18n.changeLanguage(langKey);
  }, []);

  if (isPreviewRoute) {
    return <PreviewApp />;
  }

  return (
    <StatsigProviderV2>
      <AppWrapper />
    </StatsigProviderV2>
  );
};

const AppWrapper = () => {
  const {
    i18n: { language },
  } = useTranslation();
  const logError = (error: Error, info: { componentStack: string }) => {
    airTracker.error('errorBoundary', {
      component: COMPONENT_NAME_V2,
      message: error?.message,
      stack: error?.stack,
      componentStack: info.componentStack,
    });
  };

  return (
    <React.Fragment>
      <ThemeProvider langKey={language as awxLangKey}>
        <StoreProvider>
          <ThemeDataProvider>
            <StoreProviderV2>
              <ToastProvider container={document.body}>
                <ErrorBoundary fallback={<ErrorPage />} onError={logError}>
                  <AppThemeShell>
                    <Route enableV2={true} />
                  </AppThemeShell>
                </ErrorBoundary>
              </ToastProvider>
            </StoreProviderV2>
          </ThemeDataProvider>
        </StoreProvider>
      </ThemeProvider>
    </React.Fragment>
  );
};
export default App;
